import * as echarts from "echarts";
import type { ChartConfig } from "@bi-studio/shared/lib";
import type { ChartData, ChartSize } from "./types";
import {
    getChartInstance,
    setupResize,
    clearContainer,
    resolveColors,
} from "./utils";

export const renderCorrelationChart = (
    container: HTMLElement | null,
    data: ChartData,
    config: ChartConfig,
    _size?: ChartSize,
    theme: 'light' | 'dark' = 'light'
): HTMLElement | null => {
    if (!container || typeof window === "undefined") return null;

    clearContainer(container);

    // Set container size
    container.style.width = "100%";
    container.style.height = "100%";

    const chartInstance = getChartInstance(container, theme);
    const colors = resolveColors(config, theme);

    const xField = config.xField || "x";
    const yField = config.yField || "y";
    const valueField = config.valueField || "value";

    // Extract unique categories for axes
    const xCategories = Array.from(new Set(data.map(row => String(row[xField] ?? ""))));
    const yCategories = Array.from(new Set(data.map(row => String(row[yField] ?? ""))));

    // Map data to [xIndex, yIndex, value]
    const chartData = data.map(row => {
        const xVal = String(row[xField] ?? "");
        const yVal = String(row[yField] ?? "");
        const xIndex = xCategories.indexOf(xVal);
        const yIndex = yCategories.indexOf(yVal);
        const val = Number(row[valueField]) || 0;
        return [xIndex, yIndex, val];
    });

    // Determine min/max for visualMap
    const values = chartData.map(d => d[2] as number);
    const minVal = config.minValue !== undefined ? Number(config.minValue) : (values.length > 0 ? Math.min(...values) : -1);
    const maxVal = config.maxValue !== undefined ? Number(config.maxValue) : (values.length > 0 ? Math.max(...values) : 1);

    const option: echarts.EChartsOption = {
        backgroundColor: 'transparent',
        tooltip: {
            position: 'top',
            formatter: (params: any) => {
                const xName = xCategories[params.data[0]];
                const yName = yCategories[params.data[1]];
                const val = params.data[2];
                return `${xName} & ${yName}<br/>Correlation: ${val}`;
            }
        },
        grid: {
            top: '10%',
            bottom: '15%',
            left: '15%',
            right: '10%',
            containLabel: true
        },
        xAxis: {
            type: 'category',
            data: xCategories,
            splitArea: {
                show: true
            },
            axisLabel: {
                rotate: 45,
                interval: 0
            }
        },
        yAxis: {
            type: 'category',
            data: yCategories,
            splitArea: {
                show: true
            }
        },
        visualMap: {
            min: minVal,
            max: maxVal,
            calculable: true,
            orient: 'vertical',
            right: '2%',
            top: 'center',
            inRange: {
                // Use custom colors or a default divergent palette for correlation
                color: colors.length >= 2 
                    ? colors 
                    : ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8', '#ffffbf', '#fee090', '#fdae61', '#f46d43', '#d73027', '#a50026']
            },
            textStyle: {
                color: theme === 'dark' ? '#ccc' : '#333'
            }
        },
        series: [{
            name: config.title || 'Correlation',
            type: 'heatmap',
            data: chartData,
            label: {
                show: config.showLabels !== false,
                formatter: (params: any) => {
                    return params.data[2].toFixed(2);
                },
                color: theme === 'dark' ? '#eee' : '#333'
            },
            emphasis: {
                itemStyle: {
                    shadowBlur: 10,
                    shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
            }
        }]
    };

    chartInstance.setOption(option);
    setupResize(container, chartInstance);

    return container;
};
